<script setup lang="ts">
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";
import { useFAQ } from "@/composables/faq/useFAQ";
import { useI18n } from "vue-i18n";
import BaseFigureForIcon from "@/components/base/BaseFigureForIcon.vue";

const { t } = useI18n();
const {
  activeCategory,
  categories,
  filteredFaq,
  openItems,
  slideClasses,
  toggleItem,
} = useFAQ();

const swiperOptions = {
  slidesPerView: "auto",
  spaceBetween: 6,
};
</script>

<template>
  <div class="faq">
    <h1 class="faq__title h1 color-text-primary">{{ t("faq.title") }}</h1>
    <!-- Swiper для десктопов -->
    <div class="categories-scroll-container desktop">
      <swiper
        :slides-per-view="swiperOptions.slidesPerView"
        :space-between="swiperOptions.spaceBetween"
      >
        <swiper-slide
          v-for="category in categories"
          :key="category.uuid"
          class="category-btn caption-navbar color-text-primary"
          :class="slideClasses(category.uuid)"
          @click="activeCategory = category.uuid"
        >
          {{ category.name }}
        </swiper-slide>
      </swiper>
    </div>

    <!-- Стандартный скролл для мобильных -->
    <div class="categories-scroll-container mobile">
      <div class="categories">
        <button
          v-for="category in categories"
          :key="category.uuid"
          class="category-btn caption-navbar color-text-primary"
          :class="slideClasses(category.uuid)"
          @click="activeCategory = category.uuid"
        >
          {{ category.name }}
        </button>
      </div>
    </div>

    <div class="faq-list" v-if="filteredFaq.length">
      <div v-if="filteredFaq.length" class="faq-list__wrapper">
        <div v-for="faq in filteredFaq" :key="faq.uuid" class="faq-item">
          <div
            class="faq-item__header"
            @click="toggleItem(faq.uuid)"
            :class="{ 'faq-item__header--active': openItems[faq.uuid] }"
          >
            <h4 class="faq-item__question h4 color-text-primary">
              {{ faq.question }}
            </h4>
            <BaseFigureForIcon
              icon="more"
              color="secondary"
              size="small"
              :position="openItems[faq.uuid] ? 'top' : 'bottom'"
              is-button
            />
          </div>

          <Transition name="slide-fade">
            <div v-if="openItems[faq.uuid]" class="faq-item__content">
              <div
                class="faq-item__answer caption-medium color-grey-light"
                v-html="faq.answer"
              ></div>
            </div>
          </Transition>
        </div>
      </div>

      <div v-else-if="activeCategory" class="empty-state">
        {{ t("faq.empty") }}
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "@/assets/style/colors";

.faq {
  max-width: 720px;
  margin: 39px 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0 16px;
  box-sizing: border-box;
  width: 100%;

  @media (max-width: 1100px) {
    max-width: 650px;
  }
  @media (max-width: 960px) {
    position: relative;
    width: 100%;
    margin: 40px 0 110px 0;
  }

  &__title {
    text-align: left;
    margin-bottom: 34px;
    width: 100%;

    @media (max-width: 767px) {
      margin-bottom: 20px;
      font-size: 1.5rem;
    }
  }
}

.categories-scroll-container {
  width: 100%;
  overflow-x: auto;
  margin-bottom: 20px;
  padding-bottom: 8px;
  scrollbar-width: thin;
  scrollbar-color: var(--color-grey-light);

  @media (max-width: 767px) {
    position: absolute;
    width: 94%;
    margin-top: 43px;

    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: var(--color-grey-light);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--color-grey-light);
    border-radius: 3px;

    &:hover {
      background: var(--color-grey-light);
    }
  }

  .swiper {
    width: 100%;

    .swiper-slide {
      width: auto !important;
    }
  }
}

.categories {
  display: inline-flex;
  flex-wrap: nowrap;
  gap: 6px;
  padding: 6px 0;
  min-width: 100%;
}

.category-btn {
  padding: 6px 12px;
  background-color: var(--color-white);
  border: 1px solid var(--color-grey-blue);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
  white-space: nowrap;

  &.active {
    color: var(--color-white);
    border: 1px solid var(--color-secondary);
    background: var(--color-secondary);
  }
}

.faq-list {
  border-radius: 12px;
  overflow: hidden;
  max-width: 720px;
  width: 100%;

  @media (max-width: 767px) {
    margin-top: 66px;
  }

  &__wrapper {
    display: flex;
    gap: 16px;
    flex-direction: column;
  }
}

.faq-item {
  background-color: var(--color-white);
  border-bottom: 1px solid var(--color-white);
  transition: all 0.3s ease;
  border-radius: 24px;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 26px 20px;
    cursor: pointer;
    transition: background 0.2s ease;

    &--active {
      padding: 26px 20px 15px 20px;
    }
  }

  &__content {
    overflow: hidden;
    padding: 0 20px 26px 20px;
  }

  &__answer {
    a {
      font-size: 16px;
      font-weight: 500;
      line-height: 125%;
      letter-spacing: -0.015em;
      color: var(--color-secondary);

      @media (max-width: 767px) {
        font-size: 14px;
        font-weight: 500;
        line-height: 125%;
        letter-spacing: -0.015em;
      }
    }
  }
}

.empty-state {
  text-align: center;
  padding: 2rem;
  color: var(--color-grey-light);
  font-style: italic;
}

.desktop {
  display: flex;
  @media (max-width: 767px) {
    display: none;
  }
}

.mobile {
  display: none;
  @media (max-width: 767px) {
    display: flex;
  }
}
</style>
